import styled from 'styled-components';
import { palette, theme } from 'styled-tools';

import { FontWeights, minWidth, Text } from '@calm-web/design-system';

import lockIcon from 'icons/lock-outline.svg';
import playIcon from 'icons/play-filled.svg';

// 16px (matching TitleContainer's own heading-to-first-row gap) for the space after a
// section's last row, before the next heading. Neither platform gives a row list a distinct
// trailing margin of its own: Android's PackItemDecorator only ever offsets the item AFTER
// (no bottom-edge handling), and iOS's PackRowView contributes just its own 10pt vertical
// padding, with the real gap coming from the *next* pack's leading treatment. 24px overshot
// both; matching the heading gap keeps the rhythm even without either platform's exact model.
export const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	// Matches iOS's LazyVStack(spacing: 12) between rows — the plain row shape has no internal
	// vertical padding of its own there (RowContainer below), so this is the whole gap.
	gap: 12px;
	margin-bottom: 16px;
	max-width: 1920px;
`;

// 16px matches Android's PackItemDecorator (net ~16dp header-to-first-row) and iOS's
// PackRowView (14pt) — 32px was roughly double the gap either platform actually uses.
export const TitleContainer = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 16px;
`;

// The playlist header replaces TitleContainer for `pack_type: 'playlist'` packs only —
// stacked (art above text) below tablet, side-by-side from tablet up, matching how
// ContentCard/CardGrid switch layout at the same breakpoint rather than introducing a new one.
// margin-bottom of 20px matches Android's pack_vertical_space — the confirmed spacing
// between a pack's own header content and the section below it.
export const PlaylistHeaderWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 20px;
	max-width: 1920px;

	${minWidth('tablet')} {
		align-items: center;
		flex-direction: row;
		gap: 24px;
	}
`;

export const CoverArtWrapper = styled.div`
	border-radius: 12px;
	flex-shrink: 0;
	height: 120px;
	overflow: hidden;
	position: relative;
	width: 120px;

	${minWidth('desktop')} {
		border-radius: 16px;
		height: 160px;
		width: 160px;
	}
`;

// Capped at mobileLarge, same as PlaylistControls below — the whole text column (title,
// curator link, controls, track summary) stays mobile-proportioned rather than stretching
// wide next to the cover art on larger screens.
export const PlaylistInfo = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 8px;
	max-width: 480px;
	min-width: 0;
`;

export const PlaylistTitle = styled(Text).attrs({
	el: 'h2',
	styleAs: 'h4',
	color: 'white',
	weight: FontWeights.Demi,
})`
	${minWidth('desktop')} {
		font-size: 28px;
		line-height: 36px;
	}
`;

export const TrackSummaryText = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
})`
	color: ${palette('whiteAlpha70')};
	font-size: 13px;
	// Text's <p> element carries its own default bottom margin, which stacked on top of
	// PlaylistHeaderWrapper's own margin-bottom and widened the gap to the row list below
	// past what that wrapper alone specifies. This is the last element in the header, so
	// the wrapper's margin should be the only source of that gap.
	margin-bottom: 0;
`;

// Capped at mobileLarge (480px) — the largest phone-sized viewport — and staying capped at
// every wider breakpoint too, so Play/Shuffle keep the ~50/50 mobile proportions this header
// is meant to match rather than growing wider as the page does.
export const PlaylistControls = styled.div`
	display: flex;
	gap: 12px;
	margin-top: 4px;
	max-width: 480px;
	width: 100%;
`;

const playlistControlButtonCSS = `
	align-items: center;
	border: none;
	border-radius: 24px;
	cursor: pointer;
	display: flex;
	font-size: 14px;
	font-weight: 500;
	gap: 8px;
	padding: 10px 20px;
	transition: opacity ${theme('animation.transition')};

	&:hover {
		opacity: 0.8;
	}

	&:disabled {
		cursor: default;
		opacity: 0.4;
	}
`;

// Play is the primary action for the whole playlist — solid fill, same treatment as the
// page's own "Try Calm for Free" CTA — while Shuffle stays the secondary/translucent style.
export const PlayButton = styled.button`
	${playlistControlButtonCSS}
	background: ${palette('white')};
	color: rgba(0, 0, 0, 0.85);
	flex: 1;
	justify-content: center;
`;

export const ShuffleButton = styled.button`
	${playlistControlButtonCSS}
	background: rgba(255, 255, 255, 0.12);
	color: ${palette('white')};
	flex: 1;
	justify-content: center;
`;

export const PlayIcon = styled(playIcon)`
	flex-shrink: 0;
	height: 14px;
	width: 14px;

	& path {
		fill: rgba(0, 0, 0, 0.85);
	}
`;

// No shuffle icon exists in this app's icon set — drawn directly as a stroke-based SVG
// (the standard crossing-arrows glyph) rather than adding a one-off asset for a single use.
export const ShuffleIcon = styled.svg.attrs({
	viewBox: '0 0 24 24',
	xmlns: 'http://www.w3.org/2000/svg',
	fill: 'none',
})`
	flex-shrink: 0;
	height: 14px;
	stroke: ${palette('white')};
	stroke-linecap: round;
	stroke-linejoin: round;
	stroke-width: 2;
	width: 14px;
`;

export const Title = styled(Text).attrs({
	el: 'h2',
	styleAs: 'h5',
	color: 'white',
	weight: FontWeights.Regular,
	noMargin: true,
})``;

export const Subtitle = styled(Text).attrs({
	el: 'p',
	color: 'white',
	// Text's default margin-block-start/end (0.5em each) doesn't collapse away inside
	// TitleContainer's flex layout — left un-killed, it stacked on top of TitleContainer's
	// own margin-bottom, nearly doubling the section-heading-to-first-row gap.
	noMargin: true,
})`
	max-width: 600px;
	width: 100%;
`;

// Capped at 480px to match GuideRow's own row-list precedent (Wrapper max-width: 480px in
// apps/www/src/components/app/program/GuideRow/styles.tsx) and the playlist header above it,
// rather than a wider "desktop tracklist" treatment invented without any such precedent. The
// cap lives here (not on RowContainer) so the fave heart added below is a sibling of the
// clickable row rather than nested inside it — matching GuideRow's own
// Wrapper/ClickableWrapper/FaveHeartContainer split, which is what keeps a heart click from
// also firing the row's own click handler.
export const RowWrapper = styled.div`
	align-items: center;
	display: flex;
	max-width: 480px;
`;

export const RowContainer = styled.a`
	align-items: center;
	display: flex;
	flex: 1;
	min-width: 0;
	text-decoration: none;
	transition: opacity ${theme('animation.transition')};

	&:hover {
		opacity: 0.8;
	}
`;

// Sized to the button it holds, then pulled outward by 14px, porting how Android draws this
// control: a 48dp box constrained to the row's trailing edge with layout_marginEnd="-14dp", and
// clipToPadding="false" set above it so the overhang is allowed to spill past the list's own
// horizontal gutter. Text stops short of it either way — there via a Barrier tracking the
// badge and fave, here by the box taking its own width out of the flex line.
//
// Sizing the box to the button is what keeps the spill deliberate: at 24px the button's own
// 48px overflowed to 24px past the row, which on a 500px-wide viewport put 8px of the touch
// target off-screen, silently clipped by an ancestor's overflow-x. Android's -14dp leaves its
// box 2dp inside the screen edge, and this lands in the same place.
export const FaveHeartContainer = styled.div`
	flex-shrink: 0;
	margin-inline-end: -14px;
	width: 48px;
`;

// 80px at every breakpoint matches both platforms exactly — iOS's plain-row thumbSize is a
// fixed 80pt with no size-class variance at all, and Android's real thumbnail-bearing row
// (pack_textual_horizontal_card.xml, not the image-less SimpleGuideRow) is 80dp, only bumped
// to 96dp on physical sw680dp tablets — a device-class distinction this breakpoint isn't.
// 12px corner radius matches both platforms' 12pt/12dp as well (was 8px).
export const ThumbnailWrapper = styled.div`
	border-radius: 12px;
	flex-shrink: 0;
	height: 80px;
	margin-right: 20px;
	overflow: hidden;
	position: relative;
	width: 80px;
`;

// Top-left badge, scaled down for
// this row's much smaller (56-76px) thumbnail rather than their larger tile.
export const LockIcon = styled(lockIcon)`
	background: rgba(0, 0, 0, 0.6);
	border-radius: 50%;
	height: 20px;
	left: 4px;
	padding: 3px;
	position: absolute;
	top: 4px;
	width: 20px;

	& path {
		fill: ${palette('white')};
	}
`;

export const InfoContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	min-width: 0;
`;

export const RowTitle = styled(Text).attrs({
	el: 'h3',
	styleAs: 'h6',
})`
	color: ${palette('white')};
	font-size: 14px;
	font-weight: 600;
	margin: 0 0 2px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	${minWidth('mobileLarge')} {
		font-size: 16px;
	}

	${minWidth('desktop')} {
		font-size: 18px;
	}
`;

// Wraps to a second line rather than ellipsizing at one, because both platforms let this
// line wrap: iOS sets no lineLimit on it at all (the row's VStack just grows), and Android
// only *looks* clamped — its maxLines="1" is overridden at bind time by
// allocateLinesToLabels(4, title, subtitle, description), which zeroes the line counts and
// redistributes a shared pool of 4 lines across the three labels by how each actually wrapped.
// Two here is that same budget: title (1) + description (2) + duration (1) = 4. Still clamped
// rather than unbounded, so one long description can't push the text column taller than the
// 80px thumbnail beside it and break the row rhythm.
export const RowSubtitle = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
})`
	color: ${palette('white')};
	font-size: 12px;
	font-weight: 500;
	// Text's <p> element carries its own default margin — without zeroing it out here,
	// that default (not this 2px) was the real source of the large gap between rows,
	// matching Android's/iOS's own tight ~2-4px spacing between title/description/duration.
	margin: 0 0 2px;
	overflow: hidden;

	// All three declarations are load-bearing, and they have to outrank Text's own display
	// rule — same specificity, emitted later — so the doubled selector wins that tie. Dropping
	// the bump computes display: block and stops clamping; dropping box-orient does too, even
	// with -webkit-line-clamp set. Don't trust computed display when checking this: it reports
	// flow-root while the clamp is working, so verify by measuring rendered vs scroll height.
	&& {
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		display: -webkit-box;
	}

	${minWidth('mobileLarge')} {
		font-size: 13px;
	}

	${minWidth('desktop')} {
		font-size: 14px;
	}
`;

// A third stacked, left-aligned line under title/subtitle at every breakpoint — matches
// GuideRow's own Duration (text-align: left, no right-gutter treatment at any width). No
// margin-top here — RowSubtitle's own margin-bottom already supplies the gap above; adding
// one on both sides would double it, since flex children's margins never collapse.
export const Duration = styled.span`
	color: ${palette('white')};
	display: block;
	font-size: 12px;
	font-weight: 500;
`;
